import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { isElementOf } from '../../util/react'
import { CardContent } from './card-content'
import { CardDescription } from './card-description'
import { CardHeader } from './card-header'

import styles from './card.module.css'

/**
 * @private
 * @param {string} message - The message to be logged
 * @returns {void}
 */
function warn(message) {
    console.warn(message)
}

const propTypes = {
    buttonComponent: PropTypes.func,
    children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    className: PropTypes.string,
    onButtonClick: PropTypes.func,
    title: PropTypes.string
}

/**
 * Render single Card wrapper.
 *
 * @see {@link https://confluence.ttt-sp.com/pages/viewpage.action?spaceKey=TTB&title=Molecules#Molecules-Cards}
 * @property {ReactElement} props.buttonComponent
 * @property {ReactElement} props.children - The jsx children to be rendered
 * @property {string} props.className
 * @property {string} props.title
 * @property {Function} props.onButtonClick
 * @returns {ReactElement|null}
 */
class Card extends Component {
    /**
     * @returns {ReactElement}
     */
    render() {
        const children = React.Children.toArray(this.props.children)

        return (
            <div className={cx(styles['card'], 't3sel-card', this.props.className)}>
                {this.renderHeader(children)}
                {this.renderContent(children)}
            </div>
        )
    }

    /**
     * @private
     * @param {Array<ReactElement>} children
     * @returns {ReactElement}
     */
    renderHeader(children) {
        const { buttonComponent, onButtonClick, title } = this.props
        const headers = children.filter(i => isElementOf(i, CardHeader))
        const headersLength = headers && headers.length

        if (title && !headersLength) {
            // We have an attribute "title" but no <CardHeader /> component as child, thus we return a <CardHeader />
            // with the value of the title-attribute.
            return <CardHeader className="t3sel-card-header" text={title} {...{ buttonComponent, onButtonClick }} />
        }

        if (headersLength > 1) {
            warn('More than 1 <CardHeader /> are not supported! Use only the first!')
        }

        return headers[0]
    }

    /**
     * @private
     * @param {Array<ReactElement>} children
     * @returns {ReactElement}
     */
    renderContent(children) {
        const filteredChildren = (children && children.filter(child => !isElementOf(child, CardHeader))) || []

        if (!filteredChildren.length) {
            warn('<Card /> has no children')
            return <CardContent />
        }

        if (
            filteredChildren.length <= 2 &&
            filteredChildren.every(child => isElementOf(child, CardContent) || isElementOf(child, CardDescription))
        ) {
            return filteredChildren
        }

        return <CardContent>{filteredChildren}</CardContent>
    }
}

Card.propTypes = propTypes

export { Card }
